import React, { useEffect, useState } from "react";
import "./App.css";
import "./styles/korean.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import HomePage from "./pages/HomePage";
import GalleryPage from "./pages/GalleryPage";
import PricingPage from "./pages/PricingPage";
import ReviewsPage from "./pages/ReviewsPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import PasswordResetPage from "./pages/PasswordResetPage";
import ClientDashboard from "./pages/ClientDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import BookingDetailsPage from "./pages/BookingDetailsPage";
import MaintenancePage from "./pages/MaintenancePage";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/toaster";
import MaintenanceGuard from "./components/MaintenanceGuard";

// Composants PWA
import PWAInstaller from "./components/PWAInstaller";
import NetworkStatus from "./components/NetworkStatus";
import UpdatePrompt from "./components/UpdatePrompt";

// Nettoyage automatique des données corrompues
const cleanupCorruptedData = () => {
  try {
    // Vérifier si les données sont corrompues
    const token = localStorage.getItem('auth_token');
    const cachedUser = localStorage.getItem('cached_user');
    
    if (cachedUser) {
      try {
        JSON.parse(cachedUser);
      } catch (e) {
        // console.log('🧹 Nettoyage des données utilisateur corrompues');
        localStorage.removeItem('cached_user');
        localStorage.removeItem('user_cache_timestamp');
      }
    }
    
    // Nettoyer les tokens invalides
    if (token && (token === 'undefined' || token === 'null' || token.length < 10)) {
      // console.log('🧹 Nettoyage du token invalide');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('cached_user');
      localStorage.removeItem('user_cache_timestamp');
    }
    
  } catch (error) {
    console.error('Erreur lors du nettoyage:', error);
    // En cas d'erreur localStorage, tout nettoyer
    try {
      localStorage.clear();
    } catch (e) {
      console.error('Impossible de nettoyer localStorage:', e);
    }
  }
};

// Fonction de détection PWA
const detectPWA = () => {
  try {
    // PRIORITÉ 1: Paramètre URL explicite (le plus fiable)
    const urlParams = new URLSearchParams(window.location.search);
    const isPWAParam = urlParams.get('pwa') === 'true';
    
    // Si le paramètre pwa=true est présent, c'est définitivement une PWA
    if (isPWAParam) {
      // console.log('🔍 PWA détectée via paramètre URL: pwa=true');
      return true;
    }
    
    // PRIORITÉ 2: Vérification standalone uniquement si pas de paramètre pwa=false
    const isPWAFalse = urlParams.get('pwa') === 'false';
    if (isPWAFalse) {
      // console.log('🔍 Mode PWA explicitement désactivé via paramètre URL: pwa=false');
      return false;
    }
    
    // PRIORITÉ 3: Détection standalone (plus conservatrice)
    const isStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = window.navigator && window.navigator.standalone === true;
    
    // Vérification supplémentaire: s'assurer qu'on est vraiment dans une PWA installée
    const isRealPWA = (isStandalone || isIOSStandalone) && 
                      (window.location.protocol === 'https:' || window.location.hostname === 'localhost');
    
    if (isRealPWA) {
      // console.log('🔍 PWA détectée via display-mode standalone');
      return true;
    }
    
    // console.log('🔍 Mode web normal détecté');
    return false;
  } catch (error) {
    console.error('Erreur détection PWA:', error);
    return false;
  }
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

function App() {
  const [isPWA, setIsPWA] = useState(false);

  // Détecter si on est dans une PWA installée
  useEffect(() => {
    const pwaDetected = detectPWA();
    setIsPWA(pwaDetected);
    
    // console.log('PWA Detection:', pwaDetected);
  }, []);

  // Nettoyage automatique au démarrage
  useEffect(() => {
    cleanupCorruptedData();
  }, []);

  // Composant pour rediriger les pages publiques si on est dans une PWA
  const PWAGuardRoute = ({ children }) => {
    if (isPWA) {
      return <Navigate to="/connexion" replace />;
    }
    return children;
  };
  
  return (
    <div className="App">
      <AuthProvider>
        <MaintenanceGuard>
          {/* Indicateur de statut réseau */}
          <NetworkStatus />
          
          {/* Prompt de mise à jour PWA */}
          <UpdatePrompt />
          
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Pages publiques - bloquées dans la PWA */}
              <Route 
                path="/" 
                element={
                  <PWAGuardRoute>
                    <HomePage />
                  </PWAGuardRoute>
                } 
              />
              <Route 
                path="/galerie" 
                element={
                  <PWAGuardRoute>
                    <GalleryPage />
                  </PWAGuardRoute>
                } 
              />
              <Route 
                path="/tarifs" 
                element={
                  <PWAGuardRoute>
                    <PricingPage />
                  </PWAGuardRoute>
                } 
              />
              <Route 
                path="/avis" 
                element={
                  <PWAGuardRoute>
                    <ReviewsPage />
                  </PWAGuardRoute>
                } 
              />
              
              {/* Pages d'authentification - toujours accessibles */}
              <Route path="/connexion" element={<LoginPage />} />
              <Route path="/inscription" element={<RegisterPage />} />
              <Route path="/mot-de-passe-oublie" element={<PasswordResetPage />} />
              
              {/* Espaces utilisateurs - toujours accessibles */}
              <Route path="/mon-espace" element={<ClientDashboard />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/reserver/:slotId" element={<BookingDetailsPage />} />
              
              {/* Maintenance */}
              <Route path="/maintenance" element={<MaintenancePage />} />
              
              {/* Anciennes routes */}
              <Route path="/deconnexion" element={<Navigate to={isPWA ? "/connexion" : "/"} replace />} />
              
              {/* Route catch-all */}
              <Route path="*" element={<Navigate to={isPWA ? "/connexion" : "/"} replace />} />
            </Routes>
          </BrowserRouter>
          
          {/* Toaster pour les notifications */}
          <Toaster />
          
          {/* Installer PWA (affiché uniquement si installable) */}
          <PWAInstaller />
        </MaintenanceGuard>
      </AuthProvider>
    </div>
  );
}

export default App;
